import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import events from '../data/events';

const CalendarPage = () => {
  const [currentView, setCurrentView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedFilters, setSelectedFilters] = useState({
    holiday: true,
    academic: true,
    earlyDismissal: true,
    testing: true,
    pta: true
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [language, setLanguage] = useState('en');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const liveRegionRef = useRef(null);

  // --- Color config ---
  // Uses project design tokens; earlyDismissal uses dark text on yellow for WCAG AA contrast

  const categoryColors = {
    holiday: 'bg-primary',
    academic: 'bg-orange-700',
    earlyDismissal: 'bg-accent-yellow',
    testing: 'bg-purple-700',
    pta: 'bg-teal-700'
  };

  const categoryTextColors = {
    holiday: 'text-white',
    academic: 'text-white',
    earlyDismissal: 'text-gray-900',
    testing: 'text-white',
    pta: 'text-white'
  };

  const categoryColorValues = {
    holiday: '#012060',
    academic: '#923d16',
    earlyDismissal: '#FDB71A',
    testing: '#7e22ce',
    pta: '#0f766e'
  };

  // --- Date helpers ---

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDay = new Date(year, month, 1).getDay();
    for (let i = 0; i < firstDay; i++) days.push(null);
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekDays = () => {
    const base = new Date(currentYear, currentMonth, 1);
    const start = getStartOfWeek(base);
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + currentWeekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return { weekStart, days };
  };

  const dayInRange = (day, start, end) => {
    const d = new Date(day); d.setHours(0, 0, 0, 0);
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end); e.setHours(0, 0, 0, 0);
    return d >= s && d <= e;
  };

  // --- Event helpers ---

  const getFilteredEvents = () => {
    return events.filter((event) => selectedFilters[event.category]);
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return getFilteredEvents()
      .filter((e) => {
        const end = e.endDate ? new Date(e.endDate) : new Date(e.date);
        end.setHours(23, 59, 59, 999);
        return end >= today;
      })
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  };

  const getEventsForDay = (day) => {
    return events.filter((event) => {
      if (!selectedFilters[event.category]) return false;
      const eventStart = new Date(event.date);
      const eventEnd = event.endDate ? new Date(event.endDate) : eventStart;
      return dayInRange(day, eventStart, eventEnd);
    });
  };

  const getCategoryName = (category) => {
    const names = {
      holiday: 'Holiday',
      academic: 'Academic',
      earlyDismissal: 'Early Dismissal',
      testing: 'Testing',
      pta: 'Parent & Family'
    };
    return names[category] || category;
  };

  const getCategoryNameES = (category) => {
    const names = {
      holiday: 'Feriado',
      academic: 'Académico',
      earlyDismissal: 'Salida Temprana',
      testing: 'Exámenes',
      pta: 'Padres y Familias'
    };
    return names[category] || category;
  };

  const formatDate = (dateObj) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateObj).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const formatDateRange = (event) => {
    const locale = language === 'es' ? 'es-ES' : 'en-US';
    if (!event.endDate) return formatDate(event.date);
    const start = new Date(event.date).toLocaleDateString(locale, { month: 'long', day: 'numeric' });
    const end = new Date(event.endDate).toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
    return `${start} – ${end}`;
  };

  const getEventTitle = (event) => (language === 'es' && event.titleES ? event.titleES : event.title);
  const getEventDescription = (event) => (language === 'es' && event.descriptionES ? event.descriptionES : event.description || '');

  const toggleFilter = (filter) => {
    setSelectedFilters({ ...selectedFilters, [filter]: !selectedFilters[filter] });
  };

  const getTimeDisplay = (event) => {
    if (event.allDay) return language === 'es' ? 'Todo el día' : 'All Day';
    return `${event.startTime || ''}${event.startTime && event.endTime ? ' - ' : ''}${event.endTime || ''}`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // --- Navigation ---

  const announce = (message) => {
    if (liveRegionRef.current) liveRegionRef.current.textContent = message;
  };

  const prevMonth = () => {
    if (currentView === 'week') {
      setCurrentWeekOffset(currentWeekOffset - 1);
      return;
    }
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentView === 'week') {
      setCurrentWeekOffset(currentWeekOffset + 1);
      return;
    }
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Announce month/week changes to screen readers
  useEffect(() => {
    if (currentView === 'week') {
      const { days: wd } = getWeekDays();
      announce(`Week of ${wd[0].toLocaleString('default', { month: 'long' })} ${wd[0].getDate()} to ${wd[6].toLocaleString('default', { month: 'long' })} ${wd[6].getDate()}, ${wd[6].getFullYear()}`);
    } else {
      announce(`${monthNames[currentMonth]} ${currentYear}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, currentWeekOffset, currentView]);

  // --- Modal accessibility ---

  const openEventModal = (event, buttonEl) => {
    triggerRef.current = buttonEl || document.activeElement;
    setSelectedEvent(event);
  };

  const closeModal = useCallback(() => {
    setSelectedEvent(null);
    if (triggerRef.current && triggerRef.current.focus) {
      requestAnimationFrame(() => triggerRef.current.focus());
    }
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEvent, closeModal]);

  useEffect(() => {
    if (!selectedEvent || !modalRef.current) return;
    const modal = modalRef.current;
    const focusable = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable.length > 0) focusable[0].focus();
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    modal.addEventListener('keydown', handleTab);
    return () => modal.removeEventListener('keydown', handleTab);
  }, [selectedEvent]);

  // --- Computed ---

  const days = getDaysInMonth(currentMonth, currentYear);
  const upcomingEvents = getUpcomingEvents();

  const filterOptions = [
    { key: 'holiday', label: 'Holidays', color: '#012060' },
    { key: 'academic', label: 'Academic Events', color: '#f46524' },
    { key: 'earlyDismissal', label: 'Early Dismissal', color: '#FDB71A' },
    { key: 'testing', label: 'Testing', color: '#7e22ce' },
    { key: 'pta', label: 'Parent & Family Events', color: '#0BA5A4' }
  ];

  return (
    <main className="bg-accent-light" id="main-content">
      <Navbar />

      {/* Screen reader live region */}
      <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="sr-only" />

      {/* Hero — matches site-wide pattern */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary" />
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10 bg-primary rounded-2xl py-1">
          <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
            <CalendarDaysIcon className="w-8 h-8 text-white" aria-hidden="true" />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
            School <span className="text-secondary">Calendar</span>
          </h1>
          <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
            Stay informed about school events, holidays, and important dates throughout the academic year
          </p>
        </div>
        <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
      </section>

      {/* Main Calendar Section */}
      <section className="py-16" aria-label="Calendar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-sm border border-gray-200 text-sm font-semibold text-gray-900 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-expanded={mobileFiltersOpen}
              aria-controls="calendar-sidebar"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                </svg>
                Filters &amp; Options
              </span>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${mobileFiltersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

            {/* Sidebar Filters */}
            <aside id="calendar-sidebar" className={`lg:col-span-1 ${mobileFiltersOpen ? 'block' : 'hidden'} lg:block`} aria-label="Calendar filters">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-fit sticky top-28">
                <fieldset>
                  <legend className="text-lg font-semibold text-gray-900 mb-4">Filter Events</legend>
                  <div className="space-y-3">
                    {filterOptions.map((f) => (
                      <div key={f.key} className="flex items-center">
                        <input
                          id={`filter-${f.key}`}
                          type="checkbox"
                          checked={selectedFilters[f.key]}
                          onChange={() => toggleFilter(f.key)}
                          className="h-5 w-5 text-primary rounded border-gray-300 focus:ring-primary/40"
                        />
                        <label htmlFor={`filter-${f.key}`} className="ml-2 flex items-center text-gray-700 cursor-pointer">
                          <span className="h-3 w-3 rounded-full mr-2" style={{ backgroundColor: f.color }} aria-hidden="true" />
                          {f.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>

                <div className="mt-8" role="group" aria-label="Language">
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">Language</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setLanguage('en')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md min-h-[32px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                        language === 'en' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      aria-pressed={language === 'en'}
                      aria-label="English"
                    >
                      EN
                    </button>
                    <button
                      onClick={() => setLanguage('es')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md min-h-[32px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                        language === 'es' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      aria-pressed={language === 'es'}
                      aria-label="Español"
                    >
                      ES
                    </button>
                  </div>
                </div>

                <div className="mt-8" role="group" aria-label="View options">
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">View Options</h2>
                  <div className="flex space-x-2">
                    {[
                      { key: 'month', label: 'Month' },
                      { key: 'week', label: 'Week' },
                      { key: 'list', label: 'List' }
                    ].map((v) => (
                      <button
                        key={v.key}
                        onClick={() => {
                          setCurrentView(v.key);
                          if (v.key === 'week') setCurrentWeekOffset(0);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md min-h-[32px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                          currentView === v.key
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        aria-pressed={currentView === v.key}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setCurrentMonth(new Date().getMonth());
                      setCurrentYear(new Date().getFullYear());
                      setCurrentWeekOffset(0);
                    }}
                    className="w-full mt-3 py-2 min-h-[40px] border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium"
                  >
                    Today
                  </button>
                </div>

                <div className="mt-8">
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">Upcoming Events</h2>
                  <div className="space-y-3">
                    {upcomingEvents.length > 0 ? (
                      upcomingEvents.map((event) => (
                        <button
                          key={event.id}
                          onClick={(e) => openEventModal(event, e.currentTarget)}
                          className="w-full text-left border-l-4 pl-3 py-1 min-h-[44px] hover:bg-gray-50 rounded-r transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                          style={{
                            borderColor: categoryColorValues[event.category]
                          }}
                          aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}`}
                        >
                          <div className="text-xs text-gray-600">{event.endDate ? formatDateRange(event) : formatDate(event.date)}</div>
                          <div className="text-sm font-medium text-gray-900">{getEventTitle(event)}</div>
                          <div className="text-xs text-gray-700">
                            {getTimeDisplay(event)}
                          </div>
                        </button>
                      ))
                    ) : (
                      <p className="text-xs text-gray-600 italic">No upcoming events based on your filters.</p>
                    )}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Calendar */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Calendar Header */}
                <div className="bg-primary text-white px-6 py-4 flex items-center justify-between">
                  <div>
                    {currentView === 'week' ? (
                      (() => {
                        const { days: wd } = getWeekDays();
                        const startLabel = `${wd[0].toLocaleString('default', { month: 'short' })} ${wd[0].getDate()}`;
                        const endLabel = `${wd[6].toLocaleString('default', { month: 'short' })} ${wd[6].getDate()}, ${wd[6].getFullYear()}`;
                        return <h2 className="text-base font-semibold" id="calendar-heading">Week of {startLabel} – {endLabel}</h2>;
                      })()
                    ) : (
                      <h2 className="text-base font-semibold" id="calendar-heading">{monthNames[currentMonth]} {currentYear}</h2>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={prevMonth}
                      className="p-2 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-full hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
                      aria-label="Previous"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={nextMonth}
                      className="p-2 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-full hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
                      aria-label="Next"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Weekday Headers */}
                {currentView !== 'list' && (
                  <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200" aria-hidden="true">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                      <div key={i} className="py-2 text-xs font-medium text-gray-700">
                        <abbr title={['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][i]} className="no-underline">{day}</abbr>
                      </div>
                    ))}
                  </div>
                )}

                {/* Month View */}
                {currentView === 'month' && (
                  <div className="grid grid-cols-7 auto-rows-fr border-b border-gray-200 min-h-[560px]">
                    {days.map((day, index) => {
                      const dayEvents = day ? getEventsForDay(day) : [];
                      const isToday = day && day.toDateString() === new Date().toDateString();
                      const dateLabel = day
                        ? day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                        : '';
                      return (
                        <div
                          key={index}
                          className={`border-t border-l p-1.5 sm:p-2 min-h-[80px] sm:h-32 ${day ? 'bg-white' : 'bg-gray-50 text-gray-600'} ${
                            isToday ? 'bg-secondary/10 ring-2 ring-inset ring-secondary' : ''
                          } ${index % 7 === 6 ? 'border-r' : ''}`}
                        >
                          {day && (
                            <>
                              <span className="sr-only">
                                {dateLabel}{dayEvents.length > 0 ? `, ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}
                              </span>
                              <div aria-hidden="true" className={`text-right mb-1 text-xs ${isToday ? 'font-semibold text-primary' : 'text-gray-700'}`}>
                                {day.getDate()}
                              </div>
                              <div className="space-y-0.5 sm:space-y-1 overflow-y-auto max-h-[50px] sm:max-h-[80px]">
                                {dayEvents.map((event) => (
                                  <button
                                    key={event.id}
                                    onClick={(e) => openEventModal(event, e.currentTarget)}
                                    className={`text-[10px] sm:text-[11px] w-full text-left px-1.5 sm:px-2 min-h-[24px] py-0.5 sm:py-1 rounded ${categoryColors[event.category]} ${categoryTextColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary flex items-center justify-between`}
                                    title={getEventTitle(event)}
                                    aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}`}
                                  >
                                    <span className="truncate">{getEventTitle(event)}</span>
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Week View */}
                {currentView === 'week' && (() => {
                  const { days: weekDays } = getWeekDays();
                  return (
                    <div className="border-b border-gray-200 min-h-[360px]">
                      <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200" aria-hidden="true">
                        {weekDays.map((d, i) => (
                          <div key={i} className="py-2 text-xs font-medium text-gray-700">
                            <div>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]}</div>
                            <div className="text-[10px] text-gray-600">{d.toLocaleString('default', { month: 'short' })} {d.getDate()}</div>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 auto-rows-fr min-h-[300px]">
                        {weekDays.map((day, index) => {
                          const dayEvents = getEventsForDay(day);
                          const isToday = day.toDateString() === new Date().toDateString();
                          const dateLabel = day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                          return (
                            <div
                              key={index}
                              className={`border-t border-l p-2 sm:p-3 ${index % 7 === 6 ? 'border-r' : ''} ${
                                isToday ? 'bg-secondary/10 ring-2 ring-inset ring-secondary' : 'bg-white'
                              }`}
                            >
                              <span className="sr-only">
                                {dateLabel}{dayEvents.length > 0 ? `, ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}
                              </span>
                              <div aria-hidden="true" className={`text-right mb-2 text-xs ${isToday ? 'font-semibold text-primary' : 'text-gray-700'}`}>{day.getDate()}</div>
                              <div className="space-y-2 overflow-y-auto max-h-[240px]">
                                {dayEvents.length === 0 && <div className="text-[11px] text-gray-600 italic">{language === 'es' ? 'Sin eventos' : 'No events'}</div>}
                                {dayEvents.map((event) => (
                                  <button
                                    key={event.id}
                                    onClick={(e) => openEventModal(event, e.currentTarget)}
                                    className={`text-[11px] w-full text-left px-2 min-h-[24px] py-1 rounded ${categoryColors[event.category]} ${categoryTextColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary flex items-center justify-between`}
                                    title={getEventTitle(event)}
                                    aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}`}
                                  >
                                    <span className="truncate">{getEventTitle(event)}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* List View */}
                {currentView === 'list' && (
                  <div className="border-b border-gray-200 min-h-[520px] p-4">
                    {(() => {
                      const monthEvents = getFilteredEvents()
                        .filter((event) => {
                          const start = new Date(event.date);
                          const end = event.endDate ? new Date(event.endDate) : start;
                          const monthStart = new Date(currentYear, currentMonth, 1);
                          const monthEnd = new Date(currentYear, currentMonth + 1, 0);
                          return start <= monthEnd && end >= monthStart;
                        })
                        .sort((a, b) => a.date - b.date);

                      if (monthEvents.length === 0) {
                        return (
                          <div className="text-center py-16 text-gray-600">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                            </svg>
                            <p className="text-sm font-medium">No events this month based on your filters.</p>
                          </div>
                        );
                      }

                      return (
                        <ul className="space-y-3" aria-label={`Events for ${monthNames[currentMonth]} ${currentYear}`}>
                          {monthEvents.map((event) => (
                            <li key={event.id}>
                              <button
                                onClick={(e) => openEventModal(event, e.currentTarget)}
                                className="w-full text-left flex border-l-4 p-3 min-h-[44px] bg-gray-50 rounded-r hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                                style={{
                                  borderColor: categoryColorValues[event.category]
                                }}
                                aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}, ${language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}`}
                              >
                                <div className="mr-4 text-center flex-shrink-0 w-10">
                                  <div className="font-semibold text-sm">
                                    {event.endDate
                                      ? `${event.date.getDate()}–${new Date(event.endDate).getDate()}`
                                      : event.date.getDate()}
                                  </div>
                                  <div className="text-[10px] text-gray-600">{event.date.toLocaleString('default', { month: 'short' })}</div>
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-900">{getEventTitle(event)}</div>
                                  <div className="text-[11px] text-gray-700">
                                    {getTimeDisplay(event)}
                                  </div>
                                  <div className="mt-1 text-[11px] text-gray-600 line-clamp-2">{getEventDescription(event)}</div>
                                  <div className="mt-2">
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium ${
                                        event.category === 'holiday' ? 'bg-primary/10 text-primary' :
                                        event.category === 'academic' ? 'bg-secondary/20 text-primary' :
                                        event.category === 'earlyDismissal' ? 'bg-orange-100 text-orange-900' :
                                        event.category === 'testing' ? 'bg-purple-100 text-purple-900' :
                                        'bg-teal-100 text-teal-900'
                                      }`}
                                    >
                                      {language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}
                                    </span>
                                  </div>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="modal-title" ref={modalRef}>
          <div className="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true" onClick={closeModal} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className={`${categoryColors[selectedEvent.category]} py-6 px-8`}>
                <h2 id="modal-title" className={`text-xl font-semibold ${categoryTextColors[selectedEvent.category]}`}>{getEventTitle(selectedEvent)}</h2>
                <p className={`${categoryTextColors[selectedEvent.category]} opacity-90 mt-1 text-sm`}>{selectedEvent.endDate ? formatDateRange(selectedEvent) : formatDate(selectedEvent.date)}</p>
              </div>
              <div className="bg-white px-8 py-6">
                <div className="text-gray-700 text-sm">
                  <p className="mb-3 leading-relaxed">{getEventDescription(selectedEvent)}</p>
                  {(selectedEvent.time || selectedEvent.startTime || selectedEvent.endTime || selectedEvent.allDay) && (
                    <div className="flex items-center mb-2 text-xs text-gray-600">
                      <svg className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3a.75.75 0 011.5 0v1.5h.75A2.25 2.25 0 0121 6.75v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75A2.25 2.25 0 015.25 4.5H6V3a.75.75 0 01.75-.75zM4.5 9h15v8.25a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9z" clipRule="evenodd" />
                      </svg>
                      <span>{getTimeDisplay(selectedEvent)}</span>
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="flex items-center mb-2 text-xs text-gray-600">
                      <svg className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-8 py-4 flex justify-end gap-3">
                <button onClick={closeModal} className="px-4 py-2 min-h-[40px] bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/40">
                  Close
                </button>
                {selectedEvent.link && (
                  <a
                    href={selectedEvent.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 min-h-[40px] rounded-md text-xs font-medium text-white bg-primary hover:bg-navy-600 focus:outline-none focus:ring-2 focus:ring-primary/40 inline-flex items-center"
                  >
                    More Details
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default CalendarPage;
